import React, { useMemo } from "react";

import MissionDetails from "../../components/editors/MissionDetails";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing/react";
import {
    buildActivityType,
    buildAmendment,
    buildClientInfo,
    buildContract,
    buildMission,
    buildSalaryForecast,
    buildUser,
    buildWorkerInfo,
} from "../factories";
import { ContractSignatureStatus, ProfileType } from "../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { ScrollView } from "react-native";
import { GET_SALARY_FORECAST } from "../../graphql/salaries";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";

function MissionDetailsStoryComponent({
    profileType,
    readConfirmedError,
    signerAssigned,
    awaitingSignature,
    amendmentApplied,
}: {
    profileType: ProfileType;
    readConfirmedError: boolean;
    signerAssigned: boolean;
    awaitingSignature: boolean;
    amendmentApplied: boolean;
}) {
    const user = useMemo(() => buildUser(), []);
    // BAS-2843: once an amendment is applied the back-end creates a new contract that carries the
    // amendment document, so `currentContract` and `initialContract` no longer serve the same PDF.
    const amendment = useMemo(
        () => (amendmentApplied ? buildAmendment({ applyDate: "2013-01-01" }) : null),
        [amendmentApplied],
    );
    const mission = useMemo(
        () =>
            buildMission({
                id: "uuid-uuid-uuid-uuid",
                activityType: buildActivityType({
                    title: "Mangeur de pomme | Mangeuse de pomme",
                }),
                currentContract: buildContract({
                    jobTitle: "Senior apple eater",
                    startDate: "2012-01-01",
                    endDate: "2013-04-30",
                    ...(awaitingSignature
                        ? { signatureStatus: ContractSignatureStatus.NeedSelfSign }
                        : {}),
                    ...(amendment
                        ? { contractEffectiveAsset: amendment.amendmentEffectiveAsset }
                        : {}),
                }),
                ...(amendment
                    ? { initialContractPdf: "https://www.test.io/initial-contract" }
                    : {}),
                workerInfo: buildWorkerInfo({
                    name: "Pedro Lane",
                }),
                clientInfo: buildClientInfo({
                    name: "Mercedes",
                }),
                ...(signerAssigned ? {} : { signerInfo: null }),
            }),
        [signerAssigned, awaitingSignature, amendment],
    );
    const mocks = useMemo(
        () => [
            {
                request: {
                    query: GET_SALARY_FORECAST,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        getSalaryForecast: [buildSalaryForecast()],
                    },
                }),
            },
        ],
        [],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType}>
                <MockedProvider mocks={mocks}>
                    <MissionDetails
                        mission={mission}
                        profileType={profileType}
                        readConfirmedError={readConfirmedError}
                        onUpdateMission={() => {}}
                        onEditMission={() => {}}
                        onDuplicateMission={() => {}}
                        missionAmendments={amendment ? [amendment] : []}
                    />
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
}

const meta = {
    title: "Views/Mission Details",
    component: MissionDetailsStoryComponent,
    argTypes: {
        profileType: {
            options: [ProfileType.Worker, ProfileType.Client],
            control: { type: "radio" },
        },
        readConfirmedError: {
            control: { type: "boolean" },
        },
        signerAssigned: {
            control: { type: "boolean" },
        },
        awaitingSignature: {
            control: { type: "boolean" },
        },
        amendmentApplied: {
            control: { type: "boolean" },
        },
    },
    decorators: [
        (StoryFn) => (
            <ScrollView>
                <StoryFn />
            </ScrollView>
        ),
    ],
} satisfies Meta<typeof MissionDetailsStoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
        readConfirmedError: false,
        signerAssigned: true,
        awaitingSignature: false,
        amendmentApplied: false,
    },
};

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
        readConfirmedError: false,
        signerAssigned: true,
        awaitingSignature: false,
        amendmentApplied: false,
    },
};

// BAS-2820: mission created without a signer, contract awaiting the client-side signature —
// the "change signer" CTA must be visible so a signer can be assigned after creation
export const ClientAwaitingSignatureWithoutSigner: Story = {
    args: {
        profileType: ProfileType.Client,
        readConfirmedError: false,
        signerAssigned: false,
        awaitingSignature: true,
        amendmentApplied: false,
    },
};

// BAS-2843: an amendment has been applied, so the current contract serves the amendment document.
// The "employment contract" entry must still open the initially signed contract, listed above the
// amendment rather than duplicating it.
export const WorkerWithAppliedAmendment: Story = {
    args: {
        profileType: ProfileType.Worker,
        readConfirmedError: false,
        signerAssigned: true,
        awaitingSignature: false,
        amendmentApplied: true,
    },
};
